import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X } from "lucide-react";
import { Reveal, Eyebrow } from "./Reveal";

const A = "https://customer-assets-39nsmqrw.emergentagent.net/job_327707c4-c57f-40e3-bfb7-c75b023b7589/artifacts";
const B = "https://customer-assets-jai6qajn.emergentagent.net/job_mahadinusa-trip--80rv1i07/artifacts";

const PHOTOS = [
  { src: `${A}/0ff837c265136ec4_WhatsApp%20Image%202026-06-01%20at%2020.52.19.jpeg`, alt: "Flowering tree over the turquoise pool at Mahadinusa", tall: true },
  { src: `${A}/fad2d9116cba8f26_WhatsApp%20Image%202026-06-01%20at%2020.52.19%20%281%29.jpeg`, alt: "Double room with rattan bench and timber mirror" },
  { src: `${B}/f87c1dc2bde09974_WhatsApp%20Image%202026-06-01%20at%2020.52.20.jpeg`, alt: "Family room with two double beds and a blue daybed" },
  { src: `${A}/5730c415d37fdb9e_WhatsApp%20Image%202026-06-01%20at%2020.52.20%20%282%29.jpeg`, alt: "Twin room with indigo macramé wall hanging", tall: true },
  { src: `${B}/6bf598c0d865f232_WhatsApp%20Image%202026-06-01%20at%2020.52.21%20%281%29.jpeg`, alt: "Bright bathroom with teak-framed mirror" },
  { src: `${B}/f901c14724b7fc93_WhatsApp%20Image%202026-06-01%20at%2020.52.21%20%282%29.jpeg`, alt: "Carved Mahadinusa entrance sign", tall: true },
  { src: `${A}/82024c23a725202e_WhatsApp%20Image%202026-06-01%20at%2020.52.20%20%281%29.jpeg`, alt: "Timber work desk facing the garden" },
  { src: `${B}/21742743e9977749_WhatsApp%20Image%202026-06-01%20at%2020.52.21.jpeg`, alt: "In-room amenities in recyclable paper packs" },
  { src: `${B}/e1e35f44dd0736a4_WhatsApp%20Image%202026-06-01%20at%2020.52.22.jpeg`, alt: "Teak wardrobe shelves with rolled towels" },
];

export const Gallery = () => {
  const [active, setActive] = useState(null);

  return (
    <section id="gallery" data-testid="gallery-section" className="py-20 sm:py-28">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <Reveal className="flex flex-wrap items-end justify-between gap-6 mb-12">
          <div>
            <Eyebrow>Gallery</Eyebrow>
            <h2 className="mt-4 font-serif text-2xl sm:text-3xl lg:text-4xl tracking-tight">A visual journal</h2>
          </div>
          <p className="max-w-sm text-sm text-seastone leading-relaxed">
            Warm timber, woven rattan, white linen and one very generous tree — Mahadinusa as it actually looks.
          </p>
        </Reveal>

        <div className="columns-2 md:columns-3 gap-4 [column-fill:balance]" data-testid="gallery-grid">
          {PHOTOS.map((p, i) => (
            <Reveal key={p.src} delay={(i % 3) * 0.08} className="mb-4 break-inside-avoid">
              <button
                onClick={() => setActive(p)}
                data-testid={`gallery-item-${i}`}
                className="block w-full overflow-hidden rounded-2xl group"
              >
                <img
                  src={p.src}
                  alt={p.alt}
                  loading="lazy"
                  className={`w-full object-cover transition-transform duration-700 group-hover:scale-105 ${p.tall ? "aspect-[3/4]" : "aspect-[4/3]"}`}
                />
              </button>
            </Reveal>
          ))}
        </div>
      </div>

      <AnimatePresence>
        {active && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[70] bg-ink/85 backdrop-blur grid place-items-center p-5"
            onClick={() => setActive(null)}
            data-lenis-prevent
            data-testid="gallery-lightbox"
          >
            <button
              onClick={() => setActive(null)}
              data-testid="gallery-lightbox-close"
              className="absolute top-5 right-5 h-10 w-10 grid place-items-center rounded-full bg-sand/10 text-sand border border-sand/30"
              aria-label="Close"
            >
              <X size={18} />
            </button>
            <motion.img
              initial={{ scale: 0.94, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.96, opacity: 0 }}
              src={active.src}
              alt={active.alt}
              className="max-h-[82vh] max-w-full rounded-2xl shadow-2xl"
            />
            <p className="absolute bottom-6 left-1/2 -translate-x-1/2 text-sand/80 text-xs font-mono tracking-wide text-center px-4">{active.alt}</p>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};
